import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import {
  UserPlus,
  Pencil,
  UserX,
  UserCheck,
  Eye,
  EyeOff,
  X,
  Search,
  Trash2,
} from 'lucide-react';

import { ConfirmDialog } from '../../components/ui/confirm-dialog';

const roleLabels = { owner: 'MD', sales_admin: 'Sales Admin', accountant: 'Accountant' };
const roleBadgeColors = {
  owner: 'bg-purple-100 text-purple-800',
  sales_admin: 'bg-blue-100 text-blue-800',
  accountant: 'bg-green-100 text-green-800',
};

const BOUsers = () => {
  const { user: currentUser } = useBOAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', role: 'sales_admin', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const res = await boAPI.cleanupTestData();
      const d = res.data;
      toast.success(`Cleanup done: ${d.realtors_deleted} realtors, ${d.clients_deleted} clients, ${d.estates_deleted} estates, ${d.plots_deleted + (d.plots_in_test_estates_deleted || 0)} plots removed`);
      setConfirmCleanup(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await boAPI.getUsers();
      setUsers(res.data.users);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editUser) {
        const updateData = { ...form };
        if (!updateData.password) delete updateData.password;
        await boAPI.updateUser(editUser.id, updateData);
        toast.success('User updated');
      } else {
        const res = await boAPI.createUser(form);
        toast.success(`User created. Temp password: ${res.data.temporary_password}`);
      }
      setShowForm(false);
      setEditUser(null);
      setForm({ full_name: '', email: '', phone: '', role: 'sales_admin', password: '' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (u) => {
    setEditUser(u);
    setForm({ full_name: u.full_name, email: u.email, phone: u.phone || '', role: u.role, password: '' });
    setShowForm(true);
  };

  const handleToggleActive = async (u) => {
    try {
      await boAPI.updateUser(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'User deactivated' : 'User reactivated');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update user');
    }
  };

  const filtered = users.filter((u) =>
    u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <BOLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" />
        </div>
      </BOLayout>
    );
  }

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-users-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">User Management</h1>
            <p className="text-neutral-500 mt-1">{users.length} user{users.length !== 1 ? 's' : ''} total</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setConfirmCleanup(true)}
              variant="outline"
              className="rounded-xl border-red-200 text-red-700 hover:bg-red-50"
              data-testid="cleanup-test-data-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Clean Test Data
            </Button>
            <Button
              onClick={() => { setEditUser(null); setForm({ full_name: '', email: '', phone: '', role: 'sales_admin', password: '' }); setShowForm(true); }}
              className="rounded-xl bg-primary-900 hover:bg-primary-800"
              data-testid="add-user-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" /> Add User
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
            data-testid="user-search-input"
          />
        </div>

        {/* User Form Modal */}
        {showForm && (
          <Card className="border-primary-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{editUser ? 'Edit User' : 'Add New User'}</CardTitle>
                <CardDescription>{editUser ? 'Update user details' : 'Create a new back-office user'}</CardDescription>
              </div>
              <button onClick={() => { setShowForm(false); setEditUser(null); }} className="p-2 hover:bg-neutral-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="user-form-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" data-testid="user-form-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-xl" data-testid="user-form-phone" />
                </div>
                <div>
                  <Label>Role *</Label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    data-testid="user-form-role"
                  >
                    <option value="owner">Owner</option>
                    <option value="sales_admin">Sales Admin</option>
                    <option value="accountant">Accountant</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label>{editUser ? 'New Password (leave blank to keep)' : 'Password (auto-generated if blank)'}</Label>
                  <div className="relative mt-1.5">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder={editUser ? 'Leave blank to keep current' : 'Auto-generated if blank'}
                      className="rounded-xl pr-10"
                      data-testid="user-form-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2 flex gap-3 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditUser(null); }} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="user-form-submit">
                    {submitting ? 'Saving...' : editUser ? 'Update User' : 'Create User'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        <Card className="border-neutral-border shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b bg-neutral-50">
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden sm:table-cell">Phone</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Role</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b hover:bg-neutral-50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium">{u.full_name}</td>
                    <td className="px-4 py-3 text-neutral-600">{u.email}</td>
                    <td className="px-4 py-3 text-neutral-600 hidden sm:table-cell">{u.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadgeColors[u.role] || 'bg-neutral-100'}`}>
                        {roleLabels[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={u.is_active ? 'default' : 'secondary'} className={u.is_active ? 'bg-green-100 text-green-800' : 'bg-neutral-100 text-neutral-500'}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(u)}
                          className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-600 hover:text-primary-700"
                          title="Edit"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {u.id !== currentUser?.id && (
                          <button
                            onClick={() => handleToggleActive(u)}
                            className={`p-2 rounded-lg hover:bg-neutral-100 ${u.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                            title={u.is_active ? 'Deactivate' : 'Reactivate'}
                            data-testid={`toggle-user-${u.id}`}
                          >
                            {u.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-neutral-500">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmCleanup}
        onOpenChange={(o) => !o && setConfirmCleanup(false)}
        title="Clean test data?"
        description="This will permanently delete all realtors, clients, estates and plots whose name/email/phone/plot number contains the word 'test' (case-insensitive). All linked sales, payments and document collections will also be removed. This cannot be undone."
        confirmLabel={cleaning ? 'Cleaning...' : 'Clean test data'}
        onConfirm={handleCleanup}
      />
    </BOLayout>
  );
};

export default BOUsers;
